import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { MapBrowserEvent, Overlay } from 'ol';
import Feature from 'ol/Feature';
import OlMap from 'ol/Map';
import { unByKey } from 'ol/Observable';
import View from 'ol/View';
import { defaults, ScaleLine } from 'ol/control';
import { EventsKey } from 'ol/events';
import { boundingExtent, createEmpty, createOrUpdate, extend } from 'ol/extent';
import GeoJSON from 'ol/format/GeoJSON';
import { LineString, MultiLineString, MultiPolygon, Polygon } from 'ol/geom';
import Point from 'ol/geom/Point';
import SimpleGeometry from 'ol/geom/SimpleGeometry';
import Draw, { createBox, DrawEvent, Options, SketchCoordType } from 'ol/interaction/Draw';
import Snap from 'ol/interaction/Snap';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import { fromLonLat, transformExtent } from 'ol/proj';
import { Vector as VectorSource, XYZ } from 'ol/source';
import TileSource from 'ol/source/Tile';
import { getArea, getLength } from 'ol/sphere';
import { Fill, Stroke, Text } from 'ol/style';
import CircleStyle from 'ol/style/Circle';
import Icon from 'ol/style/Icon';
import Style from 'ol/style/Style';
import { debounceTime, distinctUntilChanged, skip, Subject, Subscription, takeUntil } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { FeatureGeometry } from '../../../../models/area-item.model';
import { Geometry } from '../../../../models/general.models';
import { Layer } from '../../../../models/layer.models';
import { Project } from '../../../../models/project.module';
import { ResponseGeometry } from '../../../../models/report.model';
import { AreaService } from '../../../../services/area.service';
import { LayerService } from '../../../../services/layer.service';
import { getColormapParams, getRiskColors, isLinearLayer } from '../../../../utils';
import { basemapLayersConfig } from '../../layerConfig';
import { MapCommunicatorService } from '../../map-communicator.service';

@Component({
  selector: 'frmg-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss']
})
export class MapComponent implements OnInit, OnDestroy, OnChanges {
  @Input() reportData: Array<ResponseGeometry>;
  @Input() skippClickOnNotPoints: boolean;

  @Output() mapDrawEvent = new EventEmitter<{
    feature: FeatureGeometry | null;
    error: boolean;
  }>();
  @Output() elementClick = new EventEmitter<string | null>();
  @Output() bboxChanged = new EventEmitter<number[]>();
  private communicatorSubscription: Subscription;
  private destroy$ = new Subject<void>();
  private olEventKeys: EventsKey[] = [];
  private mapTargetResetTimeout: ReturnType<typeof setTimeout> | null = null;

  ngOnChanges(changes: SimpleChanges) {
    if (changes['reportData'] && this.map) {
      this.addReportDataToMap();
    }
  }

  private mapLayerUrl = environment.mapServerBaseUrl;
  private basemapLayers = basemapLayersConfig;

  readonly defaultMapCenter = [-90.09579004434322, 55.13439611492869];
  readonly MAX_ZOOM = 17;

  public colors: string[] = getRiskColors();
  public colorsHovered: string[] = [
    '#72706C',
    '#9D8429',
    '#934C18',
    '#802225',
    '#2E0704'
  ];
  public colorsBorder: string[] = [
    '#93918D',
    '#CFB146',
    '#C57336',
    '#9F3338',
    '#430A06'
  ];
  public focused_id: string | null;
  public focusedTimeout;

  private shadowLandcover;

  private drawSource: VectorSource = new VectorSource({
    wrapX: false
  });
  private drawLayer: VectorLayer<VectorSource> = new VectorLayer({
    source: this.drawSource,
    zIndex: 100,
    style: this.drawStyleFunction.bind(this)
  });

  private clusterStyle(size: number): Style {
    return new Style({
      image: new CircleStyle({
        radius: 16 * Math.sqrt(Math.sqrt(Math.sqrt(size))),
        stroke: new Stroke({
          color: 'rgba(94,132,247,0.3)',
          width: 4
        }),
        fill: new Fill({
          color: '#5E84F7'
        })
      }),
      // @ts-ignore
      text: new Text({
        text: size.toString(),
        fill: new Fill({
          color: '#fff'
        }),
        textAlign: 'center'
      })
    });
  }

  private selectedStyle = new Style({
    stroke: new Stroke({
      width: 4,
      color: 'rgba(0, 164, 216, 1)'
    }),
    fill: new Fill({
      color: 'rgba(0, 164, 216, 0.2)'
    })
  });
  private hoveredStyle = new Style({
    stroke: new Stroke({
      width: 4,
      color: 'rgb(45,86,209)'
    }),
    fill: new Fill({
      color: 'rgba(84,123,242,0.1)'
    })
  });
  private selectedReportStyle = new Style({
    stroke: new Stroke({
      width: 4,
      color: '#DD67FE'
    }),
    fill: new Fill({
      color: 'rgba(221,103,254,0.1)'
    })
  });

  private selectedPoint = new Style({
    image: new Icon({
      src: '../../assets/icons/map-point-active.svg'
    })
  });
  private hoveredPoint = new Style({
    image: new Icon({
      src: '../../assets/icons/map-point-hover.svg'
    })
  });
  private iconUrl = '../../assets/icons/map-point-inactive.svg';
  private iconStyle = new Style({
    image: new Icon({
      src: this.iconUrl
    })
  });
  private defaultStyle = new Style({
    stroke: new Stroke({
      width: 2,
      color: '#5E84F7'
    }),
    fill: new Fill({
      color: 'rgba(255, 255, 255, 0.4)'
    }),
    image: new Icon({
      src: '../../assets/icons/map-point-active.svg'
    })
  });
  private errorStyle = new Style({
    stroke: new Stroke({
      width: 2,
      color: '#EB5752'
    }),
    fill: new Fill({
      color: 'rgba(255, 255, 255, 0.4)'
    })
  });
  private defaultLineStyle = new Style({
    stroke: new Stroke({
      width: 4,
      color: '#5E84F7'
    }),
    fill: new Fill({
      color: 'rgba(255, 255, 255, 0.4)'
    })
  });

  // @ts-ignore
  private styleFunction(feature) {
    const hovered =
      this.focused_id &&
      (((feature.getProjects && feature.getProjects().area_id) ||
          feature.values_.area_id) === this.focused_id ||
        feature.getId() === this.focused_id);
    if (
      this.reportData?.length &&
      feature.getGeometry()!.getType() === 'Point'
    ) {
      const riskScore = feature.values_.risk_score || 0;
      return new Style({
        image: new CircleStyle({
          radius: 7,
          stroke: new Stroke({
            color: this.colorsBorder[riskScore]
          }),
          fill: new Fill({
            color: hovered ? '#DD67FE' : '#5E84F7'
          })
        })
      });
    }
    const isSelected =
      this.selectedFeatures.length &&
      this.selectedFeatures.indexOf(
        feature.values_.id || feature.values_.features[0].values_.id
      ) > -1;
    switch (feature.getGeometry()!.getType()) {
      case 'Point':
        if (hovered) {
          return this.hoveredPoint;
        } else if (isSelected) {
          return this.selectedPoint;
        } else {
          return this.iconStyle;
        }
      case 'MultiLineString':
      case 'LineString':
      case 'Polygon':
      case 'MultiPolygon':
        if (hovered) {
          return this.reportData && this.reportData.length
            ? this.selectedReportStyle
            : this.hoveredStyle;
        } else if (isSelected) {
          return this.reportData && this.reportData.length
            ? this.selectedReportStyle
            : this.selectedStyle;
        } else {
          return this.defaultLineStyle;
        }
      default:
        return this.defaultStyle;
    }
  }

  // @ts-ignore
  private drawStyleFunction() {
    return this.thresholdError ? this.errorStyle : this.defaultStyle;
  }

  public currentFeature;
  private map: OlMap;
  private snap: Snap;
  private selectedFeatures: string[] = [];
  private drawAction: Draw;
  private areaVectorLayer: VectorLayer<VectorSource>;
  private vectorSource: VectorSource;
  private initBasemapLayer: TileLayer<XYZ>;
  private activeLayers: { [key: string]: TileLayer<TileSource> } = {};
  private vectorSourcePoints?: VectorSource;
  private drawMode: boolean = false;
  private rectangleSelectMode: boolean = false;
  private sketch: Feature;
  private info: HTMLElement;
  private measureTooltipElement: HTMLElement | null;
  private measureTooltip: Overlay | null;
  private persistentMeasureOverlays: Overlay[] = [];
  private rectangleSelectTooltip: Overlay | null = null;
  private rectangleSelectTooltipElement: HTMLElement | null = null;
  private rectangleSelectLayer: VectorLayer<VectorSource> | null = null;
  private pointerPosition: number[] | null = null;
  private helpMsg: string;
  private outputMsg: string;
  private polygonThreshold = 60 * Math.pow(10, 6);
  private thresholdError = false;
  private continuePolygonMsg = 'Click to continue drawing the polygon';
  private continueLineMsg = 'Click to continue drawing the line';
  private currentProject: Project;
  private BboxClusters: VectorLayer<VectorSource>;
  private BboxClustersSource: VectorSource;
  private currentZoom: number;
  private drawGeometryChangeKey: EventsKey | null = null;
  private rectangleSelectDraw: Draw | null = null;

  // Helpers to keep cleanup readable and safe
  private removeOverlayIfPresent(overlay?: Overlay) {
    if (!overlay || !this.map) return;
    this.map.removeOverlay(overlay);
  }

  private removeElementIfPresent(element?: HTMLElement) {
    if (element && element.remove) element.remove();
  }

  private unbindOlKey(key?: EventsKey) {
    if (key) unByKey(key);
  }

  private unbindAllOlKeys() {
    if (!this.olEventKeys?.length) return;
    this.olEventKeys.forEach((k) => this.unbindOlKey(k));
    this.olEventKeys = [];
  }

  constructor(
    private areaService: AreaService,
    private mapCommunicatorService: MapCommunicatorService,
    private layersService: LayerService,
    private cd: ChangeDetectorRef
  ) {
  }

  ngOnDestroy(): void {
    delete this.reportData;
    this.drawMode = false;
    this.rectangleSelectMode = false;
    this.destroy$.next();
    this.destroy$.complete();
    this.communicatorSubscription?.unsubscribe();
    this.drawSource.clear();
    this.removeInteractions();
    this.removeRectangleSelectInteraction();
    if (this.focusedTimeout) clearTimeout(this.focusedTimeout);
    if (this.mapTargetResetTimeout) clearTimeout(this.mapTargetResetTimeout);
    this.removeOverlayIfPresent(this.measureTooltip);
    this.removeOverlayIfPresent(this.rectangleSelectTooltip);
    if (this.map) {
      this.map.un('singleclick' as any, this.clickListener as any);
      this.unbindAllOlKeys();
      this.map.setTarget(null);
    }
    this.areaService.clearAreaList();
  }

  ngOnInit(): void {
    this.initMap();
    if (!this.reportData?.length) {
      this.mapCommunicatorService.boundingBox
        .pipe(takeUntil(this.destroy$))
        .subscribe((bound) => {
          const extent = boundingExtent([
            fromLonLat([bound[0], bound[1]]),
            fromLonLat([bound[2], bound[3]])
          ]);
          this.map.getView().fit(extent, {
            padding: [100, 550, 100, 100],
            maxZoom: 8
          });
        });
      this.mapCommunicatorService.updateMapAreas
        .pipe(takeUntil(this.destroy$))
        .subscribe((_) => {
          this.loadAreasByBox();
        });
      this.mapCommunicatorService.currentProject
        .pipe(takeUntil(this.destroy$))
        .subscribe((resp) => {
          // Removed console.log for production
          this.currentProject = resp;
          if (this.map && this.currentProject && !this.drawMode) {
            // Removed console.log for production
            this.loadAreasByBox();
          } else if (this.map) {
            if (this.areaVectorLayer) {
              this.map.removeLayer(this.areaVectorLayer);
            }
            if (this.BboxClusters) {
              this.map.removeLayer(this.BboxClusters);
            }
          }
        });
    } else this.addReportDataToMap();
    this.mapCommunicatorService.geometryToFocus
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ geometry, center = false }) => {
        if (geometry) this.focusOnArea(geometry, center);
      });
    this.communicatorSubscription = this.mapCommunicatorService.mapCommunicator
      .pipe(
        skip(1),
        distinctUntilChanged((prev, curr) => {
          if (prev === 'clear' && curr === 'clear') {
            return false;
          }
          return prev === curr;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((action) => {
        if (action.includes('draw_')) {
          this.startDraw(action.split('_')[1]);
        } else if (action === 'selection') {
          this.drawMode = false;
          this.rectangleSelectMode = false;
          this.removeInteractions();
          this.removeRectangleSelectInteraction();
        } else if (action === 'rectangleSelect') {
          this.drawMode = false;
          this.rectangleSelectMode = true;
          this.removeInteractions();
          this.startRectangleSelect();
        } else if (action === 'clear') {
          if (this.drawGeometryChangeKey) {
            try {
              unByKey(this.drawGeometryChangeKey);
            } catch (_) {
              /* Ignore error */
            }
            this.drawGeometryChangeKey = undefined;
          }

          if (this.sketch) {
            try {
              this.drawSource.removeFeature(this.sketch);
            } catch (_) {
              /* Ignore error  */
            }
            this.sketch = null;
          }

          const features = this.drawSource.getFeatures();
          features.forEach((feature) => {
            try {
              this.drawSource.removeFeature(feature);
            } catch (_) {
              /* Ignore error  */
            }
          });
          this.drawSource.clear();

          this.thresholdError = false;

          this.helpMsg = 'Click to start drawing';
          this.outputMsg = '';
          this.pointerPosition = null;

          this.removeOverlayIfPresent(this.measureTooltip);
          this.removeElementIfPresent(this.measureTooltipElement);
          this.persistentMeasureOverlays.forEach((ov) =>
            this.removeOverlayIfPresent(ov)
          );
          this.persistentMeasureOverlays = [];

          this.createMeasureTooltip();

          this.mapDrawEvent.emit({ feature: null, error: false });

          if (this.drawLayer && this.drawLayer.getSource()) {
            this.drawLayer.getSource().changed();
          }
        } else if (action.includes('hover_')) {
          this.hoverArea(action.split('_')[1], action.split('_')[2] === 'true');
        } else if (action.includes('basemap_')) {
          this.changeBaseLayer(action.split('basemap_')[1]);
        } else if (action.startsWith('goToLocation_')) {
          const split = action.split('_');
          const coordinates = [split[1], split[2]].map((set) =>
            set.split(':').map((coord) => +coord)
          );
          const addMarker = split[3] === 'true';
          this.goToLocation(coordinates, addMarker);
        } else if (action.startsWith('goToLocationLL_')) {
          const split = action.split('_');
          const coordinates = [split[2], split[1]].map((coord) => +coord);
          const addMarker = split[3] === 'true';
          this.goToLocationLL(coordinates, addMarker);
        } else if (action === 'zoom_in') {
          const currentView = this.map.getView();
          currentView.getZoom()! + 1 < currentView.getMaxZoom() &&
          currentView.setZoom(currentView.getZoom()! + 1);
        } else if (action === 'zoom_out') {
          const currentView = this.map.getView();
          currentView.getZoom()! - 1 > currentView.getMinZoom() &&
          currentView.setZoom(currentView.getZoom()! - 1);
        } else if (action === 'refetch_areas_for_map') {
          if (this.currentProject) {
            this.loadAreasByBox();
          }
        } else if (action.startsWith('activateBookmark_')) {
          const split = action.split('_');
          const bbox = split[1].split('|').map((coord) => +coord);
          // @ts-ignore
          const extent = createOrUpdate(...bbox);

          this.map.getView().fit(transformExtent(extent, 'EPSG:4326', 'EPSG:3857'), {
            // padding: [100, 550, 100, 100],
            // maxZoom: 8
          });
          this.mapCommunicatorService.mapCommunicator.next('');
        } else if (action === 'clear_layers') {
          Object.keys(this.activeLayers).forEach((key) => {
            this.activeLayers[key].setVisible(false);
          });
        }
      });
    this.mapCommunicatorService.selectedAreas
      .pipe(debounceTime(0), takeUntil(this.destroy$))
      .subscribe((newSelection) => {
        this.selectedFeatures = newSelection;
        this.focused_id = null;
        this.areaVectorLayer?.getSource().changed();
      });
    this.mapCommunicatorService.layers
      .pipe(takeUntil(this.destroy$))
      .subscribe((layers) => {
        this.addFRMGLayer(layers);
      });
    const savedLayers = Object.values(this.layersService.getSavedLayers());
    if (savedLayers.length && !this.reportData) {
      this.addFRMGLayer(savedLayers);
    }
    // this.loadAreas();
  }

  private initMap(): void {
    const mapCentre: number[] = fromLonLat(this.defaultMapCenter);
    let basemapIndex = 0;
    if (localStorage.getItem('basemap')) {
      basemapIndex = this.basemapLayers.findIndex(
        (bm) =>
          bm.layerName.toLowerCase() ===
          localStorage.getItem('basemap').toLowerCase()
      );
    }
    const layer = this.basemapLayers[basemapIndex];
    this.initBasemapLayer = new TileLayer({
      source: new XYZ({
        attributions: layer.attribution ? layer.attribution : '',
        url: layer.endpointUrl
      }),
      opacity: layer.opacity,
      visible: layer.visibleState,
      zIndex: 0
    });
    const scaleControl = new ScaleLine({
      units: 'metric',
      className: 'scale-map ol-scale-line'
    });

    this.map = new OlMap({
      layers: [this.initBasemapLayer, this.drawLayer],
      target: 'map',
      view: new View({
        center: mapCentre,
        zoom: 5,
        maxZoom: this.MAX_ZOOM
      }),
      maxTilesLoading: 256,
      controls: defaults({ zoom: false }).extend([scaleControl])
    });
    this.bboxChanged.emit(this.getBbox());
    this.map.on('moveend', _ => {
      this.bboxChanged.emit(this.getBbox());
    });
    this.currentZoom = this.map.getView().getZoom() || 8;
    this.info = document.getElementById('info');
    this.createMeasureTooltip();
    const pointerMoveKey = this.map.on('pointermove', (e) => {
      if (e.dragging) {
        if (this.info) this.info.style.visibility = 'hidden';
        this.currentFeature = undefined;
        return;
      } else if (this.drawMode) {
        this.helpMsg = 'Click to start drawing';
        if (this.sketch) {
          this.pointerPosition = e.coordinate as number[];
          const geom = this.sketch.getGeometry();
          if (geom instanceof Polygon) {
            this.helpMsg = this.continuePolygonMsg;
          } else if (geom instanceof LineString) {
            this.helpMsg = this.continueLineMsg;
          }
        }
      } else if (this.rectangleSelectMode) {
        if (this.rectangleSelectTooltip) {
          this.rectangleSelectTooltip.setPosition(e.coordinate);
        }
      } else this.displayFeatureInfo(e.pixel, e.originalEvent.target);
    });
    this.olEventKeys.push(pointerMoveKey);

    if (!this.reportData?.length) {
      let debounceTimer: ReturnType<typeof setTimeout>;

      const reloadMapData = () => {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
          const rawZoom = this.map.getView().getZoom() || 8;
          this.currentZoom = Math.round(rawZoom);
          this.loadAreasByBox();
        }, 300);
      };

      const moveEndKey = this.map.on('moveend', (_) => {
        reloadMapData();
      });

      const view = this.map.getView();
      const zoomChangeKey = view.on('change:resolution', () => {
        reloadMapData();
      });

      this.olEventKeys.push(moveEndKey, zoomChangeKey);
    }
    this.mapTargetResetTimeout = setTimeout(() => {
      if (this.map) {
        this.map.setTarget(null);
        this.map.setTarget('map');
        const loadStartKey = this.map.on('loadstart', () => {
          this.map.getTargetElement().classList.add('spinner');
        });
        const loadEndKey = this.map.on('loadend', () => {
          this.map.getTargetElement().classList.remove('spinner');
        });
        this.olEventKeys.push(loadStartKey, loadEndKey);

        setTimeout(() => {
          if (this.currentProject) {
            this.loadAreasByBox();
          }
        }, 1000);
      }
    }, 500);
  }

  public addReportDataToMap() {
    const arrayPoints = [];
    this.reportData.forEach((geometry) => {
      if (geometry) {
        const feature = {
          type: 'Feature',
          geometry: geometry.geometry_data,
          properties: geometry.properties,
          id: geometry.id
        };
        arrayPoints.push(feature);
      }
    });
    const collection = {
      type: 'FeatureCollection',
      features: arrayPoints
    };
    this.addCollectionToMap(collection, true);
  }

  private isLoadingAreas = false;

  private loadAreasByBox() {
    if (this.isLoadingAreas) {
      return;
    }

    const CLUSTER_THRESHOLD = 14;

    if (this.currentZoom < CLUSTER_THRESHOLD) {
      if (this.areaVectorLayer) {
        this.map.removeLayer(this.areaVectorLayer);
      }

      if (!this.currentProject?.project_id) {
        if (this.BboxClusters) {
          this.map.removeLayer(this.BboxClusters);
          this.BboxClusters = null;
        }
        return;
      }

      const bbox = this.getBbox();
      const bounds = {
        west: bbox[0],
        south: bbox[1],
        east: bbox[2],
        north: bbox[3]
      };

      const folderId = this.currentProject?.is_project
        ? undefined
        : this.currentProject?.id;

      this.isLoadingAreas = true;
      this.areaService
        .getClusters(
          this.currentProject.project_id,
          this.currentZoom,
          bounds,
          folderId
        )
        .subscribe({
          next: (resp) => {
            this.isLoadingAreas = false;
            if (!resp.clusters || resp.clusters.length === 0) {
              if (this.BboxClusters) {
                this.map.removeLayer(this.BboxClusters);
                this.BboxClusters = null;
              }
              return;
            }

            const collectionPoints = resp.clusters.map((cluster) => {
              const point = new Point([
                cluster.centroid.lon,
                cluster.centroid.lat
              ]);
              point.transform('EPSG:4326', 'EPSG:3857');
              const feature = new Feature({
                name: cluster.count,
                geometry: point,
                isCluster: true,
                clusterData: cluster
              });
              feature.setStyle(this.clusterStyle(cluster.count));
              return feature;
            });

            this.BboxClustersSource = new VectorSource({
              format: new GeoJSON(),
              features: collectionPoints
            });
            if (this.BboxClusters) {
              this.map.removeLayer(this.BboxClusters);
            }
            this.BboxClusters = new VectorLayer({
              source: this.BboxClustersSource,
              zIndex: 100
            });
            this.map.addLayer(this.BboxClusters);
            this.BboxClusters.setZIndex(999);
            this.selectAreaFunctionality();
          },
          error: (err) => {
            this.isLoadingAreas = false;
            console.error('Error loading clusters:', err);
            if (this.BboxClusters) {
              this.map.removeLayer(this.BboxClusters);
              this.BboxClusters = null;
            }
          }
        });
    } else {
      if (this.BboxClusters) {
        this.map.removeLayer(this.BboxClusters);
      }

      if (this.currentProject && this.currentProject.project_id) {
        const bbox = this.getBbox();
        this.isLoadingAreas = true;
        this.areaService
          .getAreasByBbox(
            bbox,
            this.currentProject.project_id,
            this.currentProject.id
          )
          .subscribe({
            next: (features) => {
              this.isLoadingAreas = false;
              if (features.length === 0) {
                this.clearAreaLayers();
                return;
              }

              const transformedFeatures = features.map((area) => ({
                type: 'Feature',
                id: area.id,
                geometry: area.geometry,
                properties: {
                  id: area.id,
                  name: area.name,
                  ...area.properties
                }
              }));

              const collection = {
                type: 'FeatureCollection',
                features: transformedFeatures
              };
              this.addCollectionToMap(collection);
            },
            error: (err) => {
              this.isLoadingAreas = false;
              console.error('Error loading areas by bbox:', err);
              this.clearAreaLayers();
            }
          });
      } else {
        this.clearAreaLayers();
      }
    }
  }

  private getBbox() {
    const extent = this.map.getView().calculateExtent(this.map.getSize());
    return transformExtent(extent, 'EPSG:3857', 'EPSG:4326');
  }

  private clearAreaLayers() {
    if (this.areaVectorLayer) {
      this.map.removeLayer(this.areaVectorLayer);
      this.areaVectorLayer = null;
    }

    if (this.BboxClusters) {
      this.map.removeLayer(this.BboxClusters);
      this.BboxClusters = null;
    }
  }

  public formatLength(length: number) {
    let output: string;
    if (length > 100) {
      output = `${Math.round((length / 1000) * 100) / 100} ` + 'km';
    } else {
      output = `${Math.round(length * 100) / 100} ` + 'm';
    }
    return output;
  }

  public formatArea(area) {
    let output;
    if (area > 10000) {
      output =
        `${Math.round((area / 1000000) * 100) / 100} ` + 'km<sup>2</sup>';
    } else {
      output = `${Math.round(area * 100) / 100} ` + 'm<sup>2</sup>';
    }

    return output;
  }

  private createMeasureTooltip() {
    this.removeOverlayIfPresent(this.measureTooltip);
    this.removeElementIfPresent(this.measureTooltipElement);
    this.measureTooltipElement = document.createElement('div');
    this.measureTooltipElement.className =
      'ol-tooltip ol-tooltip-measure frmg-measure';
    this.measureTooltip = new Overlay({
      element: this.measureTooltipElement,
      offset: [0, -15],
      positioning: 'bottom-center',
      stopEvent: false,
      insertFirst: false
    });
    this.map.addOverlay(this.measureTooltip);
  }

  private displayFeatureInfo(pixel, target) {
    if (!this.info) return;
    const feature = target.closest('.ol-control')
      ? undefined
      : this.map.forEachFeatureAtPixel(
        pixel,
        (feature) => {
          return feature;
        },
        { hitTolerance: 0 }
      );
    // @ts-ignore
    if (feature && !this.drawMode && feature.get('features')) {
      const size = feature.get('features').length;
      this.info.style.left = `${pixel[0]}px`;
      this.info.style.top = `${pixel[1]}px`;
      if (feature !== this.currentFeature) {
        this.info.style.visibility = 'visible';
        // @ts-ignore
        this.info.innerText =
          size === 1
            ? feature.get('features')[0].values_.name ||
            feature.get('features')[0].values_.address.name
            : feature
              .get('features')
              .map((f) => f.values_.name)
              .join('\n');
      }
    } else if (feature && !this.drawMode) {
      // @ts-ignore
      const values = feature.getProperties();

      const hasValidTooltip =
        values?.['name'] ||
        values?.['address']?.name ||
        typeof values?.['segment_index'] === 'number';

      if (!hasValidTooltip) {
        this.info.style.visibility = 'hidden';
        return;
      }

      this.info.style.left = `${pixel[0]}px`;
      this.info.style.top = `${pixel[1]}px`;

      if (feature !== this.currentFeature) {
        this.info.style.visibility = 'visible';

        this.info.innerText =
          values?.['name'] ||
          values?.['address']?.name ||
          (typeof values?.['segment_index'] === 'number'
            ? `Segment #${values['segment_index'] + 1}`
            : '');
      }

      this.hoverArea(feature.getId() as string);
    } else {
      this.info.style.visibility = 'hidden';
    }
    this.cd.detectChanges();
    this.currentFeature = feature;
  }

  private changeBaseLayer = (type: string) => {
    this.map.removeLayer(this.initBasemapLayer);
    const layer = this.basemapLayers.find(
      (baseMap) => baseMap.layerName.toLowerCase() === type.toLowerCase()
    )!;
    this.initBasemapLayer = new TileLayer({
      source: new XYZ({
        attributions: layer.attribution ? layer.attribution : '',
        url: layer.endpointUrl
      }),
      opacity: layer.opacity,
      visible: layer.visibleState,
      zIndex: 0
    });
    this.map.addLayer(this.initBasemapLayer);
  };

  private startDraw(type: string) {
    this.removeInteractions();
    this.removeRectangleSelectInteraction();
    this.rectangleSelectMode = false;
    if (this.measureTooltip) {
      try {
        this.map.removeOverlay(this.measureTooltip);
      } catch (_) {
        /* Ignore error */
      }
    }
    if (this.measureTooltipElement) {
      try {
        this.measureTooltipElement.remove();
      } catch (_) {
        /* Ignore error */
      }
    }
    this.persistentMeasureOverlays.forEach((ov) =>
      this.removeOverlayIfPresent(ov)
    );
    this.persistentMeasureOverlays = [];
    this.drawMode = true;
    let drawType;
    let geometryFunction;
    switch (type) {
      case 'circle':
        drawType = 'Circle';
        geometryFunction = this.drawCircle;
        break;
      case 'Polygon':
        drawType = 'Polygon';
        break;
      case 'rectangle':
        drawType = 'Circle';
        geometryFunction = createBox();
        break;
      case 'line':
        drawType = 'LineString';
        break;
      case 'point':
      default:
        drawType = 'Point';
        break;
    }
    const options: Options = {
      source: this.drawSource,
      type: drawType
    };
    if (geometryFunction) {
      options.geometryFunction = geometryFunction;
    }
    if (drawType === 'Point') {
      options.style = this.selectedPoint;
    } else if (type === 'Polygon' || type === 'rectangle') {
      options.style = () => {
        return this.thresholdError ? this.errorStyle : this.defaultStyle;
      };
    }
    this.thresholdError = false;
    this.drawAction = new Draw(options);
    this.map.addInteraction(this.drawAction);
    this.createMeasureTooltip();
    this.drawAction.on('drawstart', (e: DrawEvent) => {
      this.drawSource.clear();
      this.mapDrawEvent.emit({ feature: null, error: false });
      this.sketch = e.feature;
      this.thresholdError = false;

      this.helpMsg = 'Click to start drawing';
      this.outputMsg = '';

      const geom = e.feature.getGeometry();
      if (
        type === 'Polygon' ||
        type === 'rectangle' ||
        geom instanceof Polygon
      ) {
        e.feature.setStyle(this.defaultStyle);
      }
      const lastOverlay = this.persistentMeasureOverlays.pop();
      if (lastOverlay) {
        try {
          this.map.removeOverlay(lastOverlay);
        } catch (_) {
          /* Ignore error */
        }
      }
      // @ts-ignore
      let tooltipCoord = e.coordinate;

      if (geom) {
        const extent = geom.getExtent();
        if (extent && extent.length >= 4) {
          this.pointerPosition = [
            (extent[0] + extent[2]) / 2,
            (extent[1] + extent[3]) / 2
          ];
        }
      }

      if (this.measureTooltipElement && this.measureTooltip) {
        this.measureTooltipElement.innerHTML = `${this.helpMsg}<br><b>${this.outputMsg}</b>`;
        if (tooltipCoord) {
          this.measureTooltip.setPosition(tooltipCoord);
        }
      }
      this.drawGeometryChangeKey = this.sketch
        .getGeometry()
        .on('change', (evt) => {
          const geom = evt.target;
          const feature = this.sketch;

          if (geom instanceof Polygon) {
            const area = getArea(geom);
            const length = getLength(geom);
            this.outputMsg = `${this.formatArea(area)} (${this.formatLength(
              length
            )})`;
            tooltipCoord = this.pointerPosition;
            if (area > this.polygonThreshold) {
              this.thresholdError = true;
              this.outputMsg +=
                '<br><span style="color: #EB5752;">You have exceeded the 60 SQ/KM limit</span>';
            } else {
              this.thresholdError = false;
            }
            feature.changed();
          }
          // Handle LineString for line drawing (show only length, not area)
          else if (geom instanceof LineString && type === 'line') {
            const length = getLength(geom);
            this.outputMsg = this.formatLength(length);
            tooltipCoord = this.pointerPosition;
            // No validation for lines, just show length
            this.thresholdError = false;
            feature.changed();
          }
          // Handle LineString during polygon/rectangle drawing (before polygon is closed)
          else if (
            geom instanceof LineString &&
            (type === 'Polygon' || type === 'rectangle')
          ) {
            const coordinates = geom.getCoordinates();
            if (coordinates.length >= 3) {
              const closedCoords = [...coordinates];
              const firstCoord = closedCoords[0];
              const lastCoord = closedCoords[closedCoords.length - 1];
              const isClosed =
                firstCoord[0] === lastCoord[0] &&
                firstCoord[1] === lastCoord[1];
              if (!isClosed) {
                closedCoords.push([...firstCoord]);
              }
              const tempPolygon = new Polygon([closedCoords]);
              const area = getArea(tempPolygon);
              const length = getLength(geom);
              this.outputMsg = `${this.formatArea(area)} (${this.formatLength(
                length
              )})`;
              tooltipCoord = this.pointerPosition;
              if (area > this.polygonThreshold) {
                this.thresholdError = true;
                this.outputMsg +=
                  '<br><span style="color: #EB5752;">You have exceeded the 60 SQ/KM limit</span>';
              } else {
                this.thresholdError = false;
              }
              feature.changed();
            } else {
              if (this.thresholdError) {
                this.thresholdError = false;
                feature.changed();
              }
            }
          }
          // }
          this.measureTooltipElement.innerHTML = `${this.helpMsg}<br><b>${this.outputMsg}</b>`;
          this.measureTooltip.setPosition(tooltipCoord);
        });
    });
    this.drawAction.on('drawend', (e: DrawEvent) => {
      const geom = e.feature.getGeometry();
      if (geom instanceof Polygon) {
        const polygonArea = getArea(geom);
        if (polygonArea > this.polygonThreshold) {
          this.thresholdError = true;
          e.feature.setStyle(this.errorStyle);
        } else {
          this.thresholdError = false;
          e.feature.setStyle(this.defaultStyle);
        }
      }

      const parser = new GeoJSON();
      const area = parser.writeFeaturesObject([e.feature], {
        featureProjection: 'EPSG:3857'
      });
      this.mapDrawEvent.emit({
        feature: area.features[0].geometry as FeatureGeometry,
        error: this.thresholdError
      });
      if (this.measureTooltipElement && this.measureTooltip) {
        this.measureTooltipElement.innerHTML = this.outputMsg;
        this.measureTooltip.setOffset([0, -7]);
      }
      if (this.measureTooltip)
        this.persistentMeasureOverlays.push(this.measureTooltip);
      this.measureTooltip = null;
      this.measureTooltipElement = null;
      this.createMeasureTooltip();
      this.sketch = null;
      if (this.drawGeometryChangeKey) {
        try {
          unByKey(this.drawGeometryChangeKey);
        } catch (_) {
          /* Ignore error */
        }
        this.drawGeometryChangeKey = undefined;
      }
    });
  }

  private drawCircle(coordinates: SketchCoordType, geometry: SimpleGeometry) {
    const center = coordinates[0];
    const last = coordinates[coordinates.length - 1];
    const dx = center[0] - last[0];
    const dy = center[1] - last[1];
    const radius = Math.sqrt(dx * dx + dy * dy);
    const rotation = Math.atan2(dy, dx);
    const newCoordinates = [];
    const numPoints = 12;
    for (let i = 0; i < numPoints; ++i) {
      const angle = rotation + (i * 2 * Math.PI) / numPoints;
      const fraction = i % 2 === 0 ? 1 : 0.5;
      const offsetX = radius * fraction * Math.cos(angle);
      const offsetY = radius * fraction * Math.sin(angle);
      // @ts-ignore
      newCoordinates.push([center[0] + offsetX, center[1] + offsetY]);
    }
    newCoordinates.push(newCoordinates[0].slice());
    if (!geometry) {
      geometry = new Polygon([newCoordinates]);
    } else {
      geometry.setCoordinates([newCoordinates]);
    }
    return geometry;
  }

  private selectAreaFunctionality(): void {
    if (this.drawMode || this.rectangleSelectMode) return;
    this.map.un('singleclick', this.clickListener as any);
    this.map.un('singleclick', this.clickListener as any);
    this.map.on('singleclick', this.clickListener as any);
  }

  private clickListener = (e: MapBrowserEvent<UIEvent>) => {
    if (this.drawMode || this.rectangleSelectMode) return;
    if (!this.reportData?.length) {
      this.BboxClusters &&
      this.BboxClusters.getFeatures(e.pixel).then((features: Feature[]) => {
        let newZoom = 9;
        const f = features[0];
        if (!f) return;
        // @ts-ignore
        const count = f.values_.name;
        if (count > 1) newZoom = this.currentZoom + 1;
        this.map.getView().setZoom(newZoom);
        // @ts-ignore
        this.map.getView().setCenter(f.getGeometry().flatCoordinates);
      });
      this.areaVectorLayer &&
      this.areaVectorLayer
        .getFeatures(e.pixel)
        .then((features: Feature[]) => {
          const f = features[0];

          if (!f) return;
          // @ts-ignore
          const value = f.values_;
          this.mapCommunicatorService.mapCommunicator.next(
            `map_select_${value.id}`
          );
        });
    } else {
      this.areaVectorLayer.getFeatures(e.pixel).then((features: Feature[]) => {
        const f = features[0];
        if (f) {
          // @ts-ignore
          this.elementClick.emit(f.values_.id);
        }
      });
    }
  };

  private removeInteractions = () => {
    this.map.removeInteraction(this.drawAction);
    this.map.removeInteraction(this.snap);
    if (this.drawGeometryChangeKey) {
      this.unbindOlKey(this.drawGeometryChangeKey);
      this.drawGeometryChangeKey = undefined;
    }
  };

  private removeRectangleSelectInteraction = () => {
    if (this.rectangleSelectDraw) {
      this.map.removeInteraction(this.rectangleSelectDraw);
      this.rectangleSelectDraw = null;
    }
    if (this.rectangleSelectLayer) {
      this.map.removeLayer(this.rectangleSelectLayer);
      this.rectangleSelectLayer = null;
    }
    this.removeOverlayIfPresent(this.rectangleSelectTooltip);
    this.removeElementIfPresent(this.rectangleSelectTooltipElement);
    this.drawSource.clear();
  };

  private startRectangleSelect(): void {
    this.removeRectangleSelectInteraction();

    this.createRectangleSelectTooltip();

    const rectangleSource = new VectorSource();
    this.rectangleSelectLayer = new VectorLayer({
      source: rectangleSource,
      style: new Style({
        stroke: new Stroke({
          color: 'rgba(0, 164, 216, 0.8)',
          width: 2,
          lineDash: [5, 5]
        }),
        fill: new Fill({
          color: 'rgba(0, 164, 216, 0.1)'
        })
      }),
      zIndex: 1000
    });
    this.map.addLayer(this.rectangleSelectLayer);

    this.rectangleSelectDraw = new Draw({
      source: rectangleSource,
      type: 'Circle',
      geometryFunction: createBox()
    });

    this.map.addInteraction(this.rectangleSelectDraw);

    this.rectangleSelectDraw.on('drawstart', () => {
      if (this.rectangleSelectTooltipElement) {
        this.rectangleSelectTooltipElement.innerHTML =
          'Click and drag to select multiple areas.';
      }
    });

    this.rectangleSelectDraw.on('drawend', (e: DrawEvent) => {
      const rectangle = e.feature.getGeometry() as Polygon;
      const extent = rectangle.getExtent();

      const intersectingFeatureIds: string[] = [];

      if (this.areaVectorLayer && this.vectorSource) {
        const featuresInExtent = this.vectorSource.getFeaturesInExtent(extent);

        featuresInExtent.forEach((feature) => {
          const geometry = feature.getGeometry();
          if (!geometry) return;

          if (rectangle.intersectsExtent(geometry.getExtent())) {
            let intersects = false;
            try {
              const geomType = geometry.getType();

              if (geomType === 'Polygon' || geomType === 'MultiPolygon') {
                const rectExtent = rectangle.getExtent();
                const checkCoordInRect = (coord: number[]) => {
                  return (
                    coord[0] >= rectExtent[0] &&
                    coord[0] <= rectExtent[2] &&
                    coord[1] >= rectExtent[1] &&
                    coord[1] <= rectExtent[3]
                  );
                };

                const coords =
                  geomType === 'Polygon'
                    ? (geometry as Polygon).getCoordinates()
                    : (geometry as MultiPolygon).getCoordinates();
                const flattenCoords = (arr: any[]): number[][] => {
                  if (!Array.isArray(arr)) return [];
                  if (Array.isArray(arr[0]) && Array.isArray(arr[0][0])) {
                    return arr.flatMap(flattenCoords);
                  }
                  if (Array.isArray(arr[0]) && typeof arr[0][0] === 'number') {
                    return arr;
                  }
                  return [];
                };

                const flatCoords = flattenCoords(coords);
                for (const coord of flatCoords) {
                  if (checkCoordInRect(coord)) {
                    intersects = true;
                    break;
                  }
                }

                if (!intersects) {
                  const rectCenter = [
                    (rectExtent[0] + rectExtent[2]) / 2,
                    (rectExtent[1] + rectExtent[3]) / 2
                  ];
                  const rectCorners = [
                    [rectExtent[0], rectExtent[1]], // bottom-left
                    [rectExtent[2], rectExtent[1]], // bottom-right
                    [rectExtent[2], rectExtent[3]], // top-right
                    [rectExtent[0], rectExtent[3]] // top-left
                  ];

                  try {
                    const polygon = geometry as Polygon;
                    const testPoints = [rectCenter, ...rectCorners];
                    for (const point of testPoints) {
                      if (polygon.intersectsCoordinate(point)) {
                        intersects = true;
                        break;
                      }
                    }
                  } catch (e) {
                    console.error(e);
                  }
                }
              } else if (
                geomType === 'LineString' ||
                geomType === 'MultiLineString'
              ) {
                const rectExtent = rectangle.getExtent();
                if (geomType === 'LineString') {
                  const lineString = geometry as LineString;
                  const coordinates = lineString.getCoordinates();
                  for (const coord of coordinates) {
                    if (
                      coord[0] >= rectExtent[0] &&
                      coord[0] <= rectExtent[2] &&
                      coord[1] >= rectExtent[1] &&
                      coord[1] <= rectExtent[3]
                    ) {
                      intersects = true;
                      break;
                    }
                  }
                } else if (geomType === 'MultiLineString') {
                  const multiLineString = geometry as MultiLineString;
                  const coordinates = multiLineString.getCoordinates();
                  for (const lineCoords of coordinates) {
                    for (const coord of lineCoords) {
                      if (
                        coord[0] >= rectExtent[0] &&
                        coord[0] <= rectExtent[2] &&
                        coord[1] >= rectExtent[1] &&
                        coord[1] <= rectExtent[3]
                      ) {
                        intersects = true;
                        break;
                      }
                    }
                    if (intersects) break;
                  }
                }
              } else if (geomType === 'Point' || geomType === 'MultiPoint') {
                const rectExtent = rectangle.getExtent();
                if (geomType === 'Point') {
                  const point = geometry as Point;
                  const coord = point.getCoordinates();
                  intersects =
                    coord[0] >= rectExtent[0] &&
                    coord[0] <= rectExtent[2] &&
                    coord[1] >= rectExtent[1] &&
                    coord[1] <= rectExtent[3];
                } else {
                  const multiPoint = geometry as any;
                  const coordinates = multiPoint.getCoordinates();
                  for (const coord of coordinates) {
                    if (
                      coord[0] >= rectExtent[0] &&
                      coord[0] <= rectExtent[2] &&
                      coord[1] >= rectExtent[1] &&
                      coord[1] <= rectExtent[3]
                    ) {
                      intersects = true;
                      break;
                    }
                  }
                }
              } else {
                intersects = true;
              }
            } catch (err) {
              intersects = true;
              console.error(err);
            }

            if (intersects) {
              const featureId =
                (feature.getId() as string) ||
                (feature.getProperties()?.['id'] as string) ||
                (feature.getProperties()?.['area_id'] as string) ||
                (feature as any).values_?.id ||
                (feature as any).values_?.area_id;
              if (featureId) {
                intersectingFeatureIds.push(String(featureId));
              }
            }
          }
        });
      }

      if (intersectingFeatureIds.length > 0) {
        this.mapCommunicatorService.rectangleSelectedAreas.next(
          intersectingFeatureIds
        );
      }

      setTimeout(() => {
        rectangleSource.clear();
        if (this.rectangleSelectLayer) {
          this.map.removeLayer(this.rectangleSelectLayer);
          this.rectangleSelectLayer = null;
        }
        if (this.rectangleSelectTooltipElement) {
          this.rectangleSelectTooltipElement.innerHTML = `Selected ${
            intersectingFeatureIds.length
          } area${intersectingFeatureIds.length !== 1 ? 's' : ''}.`;
          setTimeout(() => {
            if (this.rectangleSelectTooltip) {
              this.rectangleSelectTooltip.setPosition(undefined);
            }
          }, 2000);
        }
      }, 300);
    });

    const pointerMoveKey = this.map.on(
      'pointermove',
      (e: MapBrowserEvent<MouseEvent>) => {
        if (this.rectangleSelectMode && this.rectangleSelectTooltip) {
          this.rectangleSelectTooltip.setPosition(e.coordinate);
        }
      }
    );
    this.olEventKeys.push(pointerMoveKey);
  }

  private createRectangleSelectTooltip(): void {
    this.removeOverlayIfPresent(this.rectangleSelectTooltip);
    this.removeElementIfPresent(this.rectangleSelectTooltipElement);

    this.rectangleSelectTooltipElement = document.createElement('div');
    this.rectangleSelectTooltipElement.className =
      'ol-tooltip ol-tooltip-measure frmg-measure';
    this.rectangleSelectTooltipElement.innerHTML =
      'Click and drag to select multiple areas.';

    this.rectangleSelectTooltip = new Overlay({
      element: this.rectangleSelectTooltipElement,
      offset: [0, -15],
      positioning: 'bottom-center',
      stopEvent: false,
      insertFirst: false
    });
    this.map.addOverlay(this.rectangleSelectTooltip);
  }

  private addCollectionToMap(
    collection: Record<string, unknown>,
    fit: boolean = false
  ) {
    const proj: string = 'EPSG:4326';
    this.vectorSource = new VectorSource({
      format: new GeoJSON(),
      features: new GeoJSON().readFeatures(collection, {
        featureProjection: this.map.getView().getProjection(),
        dataProjection: proj
      })
    });

    if (this.areaVectorLayer) {
      this.map.removeLayer(this.areaVectorLayer);
    }
    this.areaVectorLayer = new VectorLayer({
      source: this.vectorSource,
      style: this.styleFunction.bind(this),
      zIndex: 100
    });

    this.map.addLayer(this.areaVectorLayer);
    this.areaVectorLayer.setZIndex(999);

    if (!this.drawMode && !this.rectangleSelectMode)
      this.selectAreaFunctionality();
    if (fit) {
      const extent = createEmpty();
      extend(extent, this.vectorSource.getExtent());
      if (extent.some((value) => value == Infinity || value == -Infinity))
        return;
      this.map.getView().fit(extent, {
        padding: [100, 100, 100, 100],
        maxZoom: this.MAX_ZOOM,
        duration: 300
      });
    }
  }

  private focusOnArea(geometry: Geometry, center?: boolean) {
    const padding = center ? [100, 100, 100, 100] : [100, 550, 100, 150];

    let targetGeometry;
    if (geometry.type === 'Point') {
      // @ts-ignore
      targetGeometry = new Point(geometry.coordinates);
    } else if (geometry.type == 'Polygon') {
      // @ts-ignore
      targetGeometry = new Polygon(geometry.coordinates);
    } else {
      // @ts-ignore
      targetGeometry = new LineString(geometry.coordinates);
    }
    // @ts-ignore
    if (targetGeometry)
      this.map
        .getView()
        .fit(targetGeometry.transform('EPSG:4326', 'EPSG:3857'), {
          minResolution: 5,
          padding,
          duration: 300
        });
  }

  private hoverArea(id: string, forever?: boolean) {
    if (id !== this.focused_id && this.focused_id) {
      // @ts-ignore
      const targetFeature =
        this.vectorSourcePoints
          ?.getFeatures()
          .find(
            (f) =>
              f.getProperties()['area_id'] === this.focused_id ||
              f.getId() === this.focused_id
          )
          ?.getGeometry() ||
        this.vectorSource
          ?.getFeatures()
          .find(
            (f) =>
              f.getProperties()['area_id'] === this.focused_id ||
              f.getId() === this.focused_id
          )
          ?.getGeometry();
      if (this.focusedTimeout) {
        clearTimeout(this.focusedTimeout);
      }
      this.focused_id = null;
      targetFeature?.changed();
    }
    // @ts-ignore
    const targetFeature =
      this.vectorSourcePoints
        ?.getFeatures()
        .find((f) => f.getProperties()['area_id'] === id || f.getId() === id)
        ?.getGeometry() ||
      this.vectorSource
        ?.getFeatures()
        .find((f) => f.getProperties()['area_id'] === id || f.getId() === id)
        ?.getGeometry();
    if (!targetFeature) return;
    this.focused_id = id;
    targetFeature.changed();
    if (this.focusedTimeout) {
      clearTimeout(this.focusedTimeout);
    }
    if (!forever)
      this.focusedTimeout = setTimeout(() => {
        this.focused_id = null;
        targetFeature && targetFeature?.changed();
      }, 1000);
  }

  private goToLocation(coordinates: number[][], addMarker?: boolean) {
    const line = new LineString(coordinates).transform(
      'EPSG:4326',
      'EPSG:3857'
    ) as SimpleGeometry;
    const pCoordinates = [
      (coordinates[0][0] + coordinates[1][0]) / 2,
      (coordinates[0][1] + coordinates[1][1]) / 2
    ];
    const point = new Point(pCoordinates).transform(
      'EPSG:4326',
      'EPSG:3857'
    ) as SimpleGeometry;
    this.map.getView().fit(line, {
      minResolution: 5,
      padding: [100, 100, 100, 150],
      duration: 300
    });
    if (addMarker) {
      this.drawSource.addFeature(
        new Feature({
          geometry: point
        })
      );
      this.mapDrawEvent.emit({
        feature: { type: 'Point', coordinates: pCoordinates },
        error: false
      });
    }
  }

  private goToLocationLL(coordinates: number[], addMarker?: boolean) {
    const point = new Point(fromLonLat(coordinates)) as SimpleGeometry;
    this.map.getView().fit(point, {
      minResolution: 5,
      padding: [100, 100, 100, 150],
      duration: 300
    });
    if (addMarker) {
      this.drawSource.addFeature(
        new Feature({
          geometry: point
        })
      );
      this.mapDrawEvent.emit({
        feature: { type: 'Point', coordinates },
        error: false
      });
    }
  }

  private addFRMGLayer = (
    layers: Array<{
      range?: {
        start: number;
        end: number;
        invert: number;
      };
      selected?: string[];
      active: boolean;
      order: number;
      layer: Layer;
    }>
  ) => {
    if (this.reportData && this.reportData.length) return;
    const psbaLayer = layers.find(
      (psbaToBe) => psbaToBe.layer.layer.name === 'pcba'
    );
    const psbaActive = psbaLayer && psbaLayer.active;
    const layersToShow = layers.filter((layer) => {
      return layer.active;
    });
    Object.keys(this.activeLayers).forEach((key) => {
      if (!layersToShow.some((layer) => layer.layer.layer.name === key)) {
        this.activeLayers[key].setVisible(false);
      }
    });
    layers.forEach((layer) => {
      if (!this.activeLayers[layer.layer.layer.name]) {
        if (layer.layer.layer.name === 'landcover' && !this.shadowLandcover) {
          const url = this.generateLayersUlr(layer);

          this.shadowLandcover = new TileLayer<TileSource>({
            source: new XYZ({
              url,
              projection: 'EPSG:3857'
            }),
            opacity: 1,
            visible: psbaActive,
            // @ts-ignore
            zIndex: 20 - psbaLayer.layer.order * 2 + 1
          });
          this.map.addLayer(this.shadowLandcover);
        }
        const colormap = getColormapParams(
          layer.layer,
          layer.layer.layer.value_type === 'CATEGORICAL',
          layer.layer.layer.name === 'disttohazard'
        );
        const url = `${this.mapLayerUrl}cog/tiles/WebMercatorQuad/{z}/{x}/{y}.png?url=file://${layer.layer.layer.tif_path}${colormap}`;
        this.activeLayers[layer.layer.layer.name] = new TileLayer<TileSource>({
          source: new XYZ({
            url,
            projection: 'EPSG:3857'
          }),
          opacity: 1,
          visible: false,
          // @ts-ignore
          zIndex: 20 - layer.order * 2
        });
        this.map.addLayer(this.activeLayers[layer.layer.layer.name]);
      } else {
        this.activeLayers[layer.layer.layer.name].setZIndex(
          20 - layer.order * 2
        );
        const url = this.generateLayersUlr(layer);
        const sourceUrl = this.activeLayers[layer.layer.layer.name].getSource();
        // @ts-ignore
        if (sourceUrl.urls[0] !== url)
          this.activeLayers[layer.layer.layer.name].setSource(
            new XYZ({
              url,
              projection: 'EPSG:3857'
              // tileLoadFunction: this.tileLoader
            })
          );
      }
    });
    if (
      !psbaActive &&
      this.shadowLandcover &&
      this.shadowLandcover.isVisible()
    ) {
      this.shadowLandcover.setVisible(false);
    } else if (
      psbaActive &&
      this.shadowLandcover &&
      !this.shadowLandcover.isVisible()
    ) {
      this.shadowLandcover.setVisible(true);
    }
    layersToShow.forEach((layer) => {
      const mapLayer = this.activeLayers[layer.layer.layer.name];
      if (psbaActive && this.shadowLandcover) {
        if (layer.layer.layer.name === 'pcba') mapLayer.setVisible(true);
      } else {
        mapLayer.setVisible(true);
      }
    });
  };

  public generateLayersUlr(layer) {
    const colormap = getColormapParams(
      layer.layer,
      layer.layer.layer.value_type === 'CATEGORICAL',
      true
    );
    let url = `${this.mapLayerUrl}cog/tiles/WebMercatorQuad/{z}/{x}/{y}.png?url=file://${layer.layer.layer.tif_path}`;
    if (isLinearLayer(layer.layer)) {
      url += colormap;
      if (layer.range.invert) {
        url += '&algorithm=invert_mask_range&algorithm_params=';
      } else {
        url += '&algorithm=mask_range&algorithm_params=';
      }
      const parmans = {
        min_value: layer.range.start,
        max_value: layer.range.end
      };
      url += encodeURIComponent(JSON.stringify(parmans));
    } else {
      url += '&colormap_type=explicit&colormap=';
      const colormaps = {};
      Object.values(layer.selected).forEach((key: string) => {
        colormaps[key] = layer.layer.layer.colormap_values[key];
      });
      url += encodeURIComponent(JSON.stringify(colormaps));
    }
    return url;
  }
}
